// src/pages/Login.tsx
//
// Fidelidade aos artboards `portal-login.dc.html` e
// `portal-login-toast-erro-auth.dc.html` (UI-2).
//
// Composição do canvas: split-screen 48% / resto. À esquerda o painel de MARCA
// (gradiente teal `#019EAD → #015B66`, dois ornamentos circulares, logo 44px,
// `.plogin-h1` Barlow 34/700, tagline 15px e três `.plogin-feat`); à direita o
// formulário de 400px com `h2` 24/800, sublinha de 13px e os campos com RÓTULO
// EXTERNO (`.plogin-label` 13px/600) sobre input de 48px — o canvas não tem label
// flutuante em lugar nenhum (DESIGN-SYSTEM.md §6.3).
//
// O teal da marca NÃO é o `primary.main` do tema: o artboard declara `--pbrand`
// como extensão local do specimen, amostrada de `icon_fiscaliza.png`. É a única
// cor deste arquivo que não sai de token, e sai exatamente como o canvas escreve.
//
// O erro de autenticação usa `toast.custom` com a caixa `.ptoast` do artboard —
// o `Snackbar` global (`src/components/snackbar`) é de outro território.

import { z } from 'zod';
import { toast } from 'sonner';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';

import { TxForm } from 'src/auditoria/components/tx-form/tx-form';

import { Field } from 'src/components/hook-form';

import { useAuth } from 'src/auth/auth-context';

import { DsIcon, DsField, DsButton, DsIconButton } from 'src/auditoria/components/ds';

import type { DsIconName } from 'src/auditoria/components/ds';

// ----------------------------------------------------------------------

const schema = z.object({
  login: z.string().min(1, 'Informe o CPF ou CNPJ'),
  senha: z.string().min(1, 'Informe a senha'),
});
type LoginForm = z.infer<typeof schema>;

/** `--pbrand` / `--pbrand-dark` do artboard (amostradas de `icon_fiscaliza.png`). */
const TEAL = '#019EAD';
const TEAL_DARK = '#015B66';
const LOGO_SRC = `${import.meta.env.BASE_URL}assets/fiscaliza/icon_fiscaliza.png`;

/**
 * `.plogin-feat` — três linhas com tile de 36px e texto de 13px.
 *
 * O canvas desenha aqui glifos que `DsIconName` ainda não tem (nota fiscal, cifrão,
 * livro de processos). Vale a regra de migração de §3.4: só traço, nunca um `-bold`
 * ou `-fill` novo — aqui, o nome de traço mais próximo de cada glifo. Os três nomes
 * que faltam estão registrados no mailbox.
 */
const DESTAQUES: { icon: DsIconName; texto: string }[] = [
  { icon: 'imprimir', texto: 'Emita e consulte suas notas fiscais' },
  { icon: 'baixar', texto: 'Veja seus débitos e gere guias de pagamento' },
  { icon: 'anexo', texto: 'Abra processos e acompanhe protocolos' },
];

// ----------------------------------------------------------------------

/**
 * `.ptoast` — caixa de 400px com faixa de 4px em `error.main`, tile de 32px,
 * título de 13px/700 e mensagem de 12px/1.5. Substitui a geometria do snackbar
 * global só nesta tela, via `toast.custom`.
 */
function ToastErroAuth({ id, mensagem }: { id: string | number; mensagem: string }) {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        // O artboard desenha 400px de largura, mas quem manda na caixa externa é o
        // `Toaster` global (`SnackbarRoot`, `width: 300`), que é de outro território.
        // Aqui a caixa ocupa a largura que ela dá, com a geometria interna do canvas.
        width: '100%',
        maxWidth: 400,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        borderRadius: '10px',
        boxShadow: (theme) => theme.vars.customShadows.dropdown,
        padding: '14px 12px 14px 14px',
      }}
    >
      <Box
        sx={[
          {
            width: 32,
            height: 32,
            flex: '0 0 32px',
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'error.lighter',
            color: 'error.dark',
          },
          (theme) =>
            theme.applyStyles('dark', {
              color: theme.vars.palette.error.light,
            }),
        ]}
      >
        <DsIcon name="info" size={18} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box component="span" sx={{ display: 'block', fontSize: 13, fontWeight: 700 }}>
          Não foi possível entrar
        </Box>
        <Box
          component="span"
          sx={{
            display: 'block',
            fontSize: 12,
            lineHeight: 1.5,
            color: 'text.secondary',
            mt: '4px',
          }}
        >
          {mensagem}
        </Box>
      </Box>

      <DsIconButton icon="fechar" aria-label="Fechar aviso" onClick={() => toast.dismiss(id)} />
    </Box>
  );
}

// ----------------------------------------------------------------------

/**
 * `.plogin-inwrap input`: 48px de altura, borda `grey.400`, raio 8 e 14px —
 * geometria local do artboard de login, mais alta que a `.fieldbox` de §6.3.
 * `.plogin-label`: 13px/600 em `text.primary`, 7px até o campo.
 */
const SX_CAMPO_LOGIN = {
  mb: '20px',
  '& > span:first-of-type': {
    fontSize: 13,
    fontWeight: 600,
    color: 'text.primary',
    mb: '7px',
  },
  '& .MuiOutlinedInput-root': { fontSize: 14, minHeight: 48 },
  '& .MuiOutlinedInput-input': { padding: '12px 14px' },
  '& fieldset': { borderColor: 'grey.400' },
  // `.ferr` (§6.3): 11px/600 sem o recuo lateral do FormHelperText.
  '& .MuiFormHelperText-root': {
    fontSize: 11,
    fontWeight: 600,
    margin: '6px 0 0',
  },
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.login.replace(/\D/g, ''), data.senha);
      navigate('/', { replace: true });
    } catch (e) {
      // Copy do canvas (portal-login-toast-erro-auth): título + descrição.
      const mensagem =
        e instanceof Error
          ? e.message
          : 'CPF/CNPJ ou senha inválidos. Confira os dados e tente novamente.';

      toast.custom((id) => <ToastErroAuth id={id} mensagem={mensagem} />);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
      }}
    >
      {/* Painel de marca. Abaixo de `md` ele sai e a marca volta como linha compacta
          no topo do formulário — os artboards são 1440x900 e não dizem nada aqui;
          o comportamento é inventado e está registrado no mailbox. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '48%',
          flex: '0 0 48%',
          padding: '48px',
          color: 'common.white',
          background: `linear-gradient(150deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* `.plogin-orn` — dois círculos de branco a 6%. */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 460,
            height: 460,
            borderRadius: '50%',
            right: -160,
            top: -160,
            bgcolor: 'rgba(255,255,255,0.06)',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            left: -120,
            bottom: -120,
            bgcolor: 'rgba(255,255,255,0.06)',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          {/* O canvas desenha um "F" em Barlow porque o artboard não embute imagem;
              o app já tem o brandmark real e ele fica. */}
          <Box
            sx={{
              width: 44,
              height: 44,
              flex: '0 0 44px',
              borderRadius: '12px',
              bgcolor: 'common.white',
              display: 'grid',
              placeItems: 'center',
              padding: '6px',
            }}
          >
            <Box component="img" src={LOGO_SRC} alt="Fiscaliza" sx={{ width: 1, height: 1 }} />
          </Box>
          <Box sx={{ fontSize: 18, fontWeight: 700 }}>Fiscaliza</Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Box
            component="h1"
            sx={(theme) => ({
              fontFamily: theme.typography.fontSecondaryFamily,
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              lineHeight: 1.5,
              m: '0 0 12px',
              maxWidth: 420,
            })}
          >
            Portal do Contribuinte
          </Box>

          <Box
            component="p"
            sx={{
              fontSize: 15,
              lineHeight: 1.55,
              m: '0 0 32px',
              color: 'rgba(255,255,255,0.85)',
              maxWidth: 420,
            }}
          >
            Seus serviços tributários com o município, num só lugar — a qualquer hora.
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {DESTAQUES.map((destaque) => (
              <Box key={destaque.texto} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    flex: '0 0 36px',
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.14)',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'common.white',
                  }}
                >
                  <DsIcon name={destaque.icon} size={20} />
                </Box>
                <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.92)' }}>{destaque.texto}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            fontSize: 11,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          Prefeitura Municipal de Ipatinga — MG
        </Box>
      </Box>

      {/* Formulário */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: '24px', sm: '48px' },
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ width: 1, maxWidth: 400 }}>
          {/* Marca compacta abaixo de `md`, onde o painel de marca não aparece. */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: '12px',
              mb: '32px',
            }}
          >
            <Box component="img" src={LOGO_SRC} alt="Fiscaliza" sx={{ width: 40, height: 40 }} />
            <Box sx={{ fontSize: 18, fontWeight: 700 }}>Fiscaliza</Box>
          </Box>

          <Box
            component="h2"
            sx={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              m: '0 0 4px',
            }}
          >
            Bem-vindo
          </Box>
          <Box component="p" sx={{ fontSize: 13, color: 'text.secondary', m: '0 0 32px' }}>
            Acesse com seu CPF ou CNPJ para continuar.
          </Box>

          <TxForm<LoginForm>
            schema={schema}
            defaultValues={{ login: '', senha: '' }}
            onSubmit={onSubmit}
          >
            <DsField label="CPF / CNPJ" sx={SX_CAMPO_LOGIN}>
              <Field.Text name="login" placeholder="000.000.000-00" autoFocus />
            </DsField>

            <DsField label="Senha" sx={SX_CAMPO_LOGIN}>
              <Field.Text
                name="senha"
                type={mostrarSenha ? 'text' : 'password'}
                slotProps={{
                  input: {
                    // `.plogin-eye`: botão-ícone sem borda encostado na direita do campo.
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: '-6px' }}>
                        <DsIconButton
                          icon="olho"
                          tone="bare"
                          aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                          onClick={() => setMostrarSenha((v) => !v)}
                        />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </DsField>

            {/* `.plogin-btn`: 48px de altura, largura total, no teal da marca. */}
            <DsButton
              type="submit"
              size="tall"
              sx={{
                width: '100%',
                height: 48,
                fontSize: 14,
                mt: '4px',
                bgcolor: TEAL,
                '&:hover': { bgcolor: TEAL_DARK },
              }}
            >
              Entrar
            </DsButton>
          </TxForm>

          <Box
            component="p"
            sx={{
              fontSize: 12,
              color: 'text.disabled',
              textAlign: 'center',
              m: '32px 0 0',
            }}
          >
            Problemas para acessar? Procure a Prefeitura Municipal de Ipatinga — MG.
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
